import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import { useAuth } from '../App';
import { API, getImageUrl } from '../utils';
import { toast } from 'sonner';
import { ArrowLeft, Send, Briefcase, Building2, Paperclip, X, FileText } from 'lucide-react';

const Messages = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { partnerId: urlPartnerId } = useParams();
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(urlPartnerId || location.state?.partnerId || null);
  const [selectedPartnerInfo, setSelectedPartnerInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showChat, setShowChat] = useState(!!urlPartnerId);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  const fetchPartnerInfo = useCallback(async (partnerId) => {
    try {
      const response = await axios.get(`${API}/users/${partnerId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSelectedPartnerInfo(response.data);
    } catch (error) {
      console.error('Error fetching partner info:', error);
    }
  }, [token]);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/messages`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setConversations(response.data);
      if (selectedPartner) {
        const existingConvo = response.data.find(c => c.partner_id === selectedPartner);
        if (!existingConvo) fetchPartnerInfo(selectedPartner);
      } else if (response.data.length > 0) {
        setSelectedPartner(response.data[0].partner_id);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [token, selectedPartner, fetchPartnerInfo]);

  const fetchMessages = useCallback(async (partnerId) => {
    try {
      const response = await axios.get(`${API}/messages/${partnerId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [token]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  useEffect(() => {
    if (urlPartnerId && urlPartnerId !== selectedPartner) {
      setSelectedPartner(urlPartnerId);
      setShowChat(true);
    }
  }, [urlPartnerId]);

  const handleSelectConversation = (partnerId) => {
    setSelectedPartner(partnerId);
    setShowChat(true);
    navigate(`/messages/${partnerId}`, { replace: true });
  };

  const handleBackToList = () => {
    setShowChat(false);
    navigate('/messages', { replace: true });
  };

  useEffect(() => {
    if (selectedPartner) {
      fetchMessages(selectedPartner);
      const interval = setInterval(() => {
        fetchMessages(selectedPartner);
        fetchConversations();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedPartner, fetchMessages, fetchConversations]);

  useEffect(() => { scrollToBottom(); }, [messages]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    setPendingFile({ file, previewUrl: isImage ? URL.createObjectURL(file) : null, name: file.name });
    e.target.value = '';
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !pendingFile) return;
    if (!selectedPartner) return;
    setSending(true);
    let file_url = null;
    let file_name = null;
    try {
      if (pendingFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', pendingFile.file);
        const uploadRes = await axios.post(`${API}/upload/message-attachment`, formData, {
          withCredentials: true,
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'multipart/form-data' }
        });
        file_url = uploadRes.data.url;
        file_name = uploadRes.data.file_name;
        setUploading(false);
      }
      await axios.post(`${API}/messages`, {
        receiver_id: selectedPartner,
        content: newMessage || (file_name ? '' : ''),
        file_url,
        file_name
      }, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setNewMessage('');
      setPendingFile(null);
      fetchMessages(selectedPartner);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const selectedConversation = conversations.find(c => c.partner_id === selectedPartner);

  const getPartnerDisplayInfo = () => {
    if (selectedConversation) return { name: selectedConversation.partner_name, role: selectedConversation.partner_role, id: selectedConversation.partner_id };
    if (selectedPartnerInfo) return { name: selectedPartnerInfo.name, role: selectedPartnerInfo.role, id: selectedPartner };
    return null;
  };

  const partnerDisplay = getPartnerDisplayInfo();

  const getInitials = (name) => name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#f7f5f2]">
        <div className="animate-pulse text-[#a0a0a0] text-sm">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="fixed top-[56px] left-0 right-0 bottom-0 flex overflow-hidden md:static md:h-[100dvh]">

      {/* ── Conversation List ── */}
      <div className={`${showChat ? 'hidden md:flex' : 'flex'} w-full md:w-[300px] flex-shrink-0 flex-col border-r border-[#e4e1db] bg-white`}>
        {/* List header */}
        <div className="px-5 pt-5 pb-3.5 border-b border-[#e4e1db]">
          <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0]">Inbox</p>
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          {conversations.length > 0 ? (
            conversations.map((conv) => (
              <button
                key={conv.partner_id}
                onClick={() => handleSelectConversation(conv.partner_id)}
                className={`w-full flex items-start gap-3 px-[18px] py-3.5 border-b border-[#e4e1db] text-left transition-colors ${
                  selectedPartner === conv.partner_id
                    ? 'bg-[#f7f5f2] border-l-[3px] border-l-[#0e0e0e]'
                    : 'hover:bg-[#f7f5f2]'
                }`}
                data-testid={`conversation-${conv.partner_id}`}
              >
                {/* Avatar */}
                <div className="w-9 h-9 rounded-full bg-[#0e0e0e] text-white flex items-center justify-center text-[13px] font-semibold flex-shrink-0 overflow-hidden">
                  {conv.partner_picture
                    ? <img src={getImageUrl(conv.partner_picture)} alt={conv.partner_name} className="w-full h-full object-cover" />
                    : getInitials(conv.partner_name)
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[13px] font-semibold text-[#0e0e0e] truncate">{conv.partner_name}</p>
                    {conv.unread > 0 && (
                      <span className="w-4 h-4 rounded-full bg-[#f87171] text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                        {conv.unread}
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-[#a0a0a0] truncate max-w-[180px] mt-0.5">
                    {conv.messages?.[0]?.content || 'No messages yet'}
                  </p>
                </div>
              </button>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center h-[200px] text-center px-6">
              <div className="text-[28px] opacity-20 mb-2.5">
                <svg width="32" height="32" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1"><circle cx="10" cy="7" r="4"/><path d="M2 18c0-3.5 3.6-6 8-6s8 2.5 8 6"/></svg>
              </div>
              <p className="text-[13px] text-[#a0a0a0]">No conversations yet</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Chat Pane ── */}
      <div className={`${showChat ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-white overflow-hidden`}>
        {selectedPartner && partnerDisplay ? (
          <>
            {/* Chat header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#e4e1db] flex-shrink-0 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                {/* Mobile back */}
                <button onClick={handleBackToList} className="md:hidden text-[#5a5a5a] hover:text-[#0e0e0e] transition-colors mr-1">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-9 h-9 rounded-full bg-[#0e0e0e] text-white flex items-center justify-center text-[13px] font-semibold overflow-hidden flex-shrink-0">
                  {selectedConversation?.partner_picture
                    ? <img src={getImageUrl(selectedConversation.partner_picture)} alt="" className="w-full h-full object-cover" />
                    : getInitials(partnerDisplay.name)
                  }
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-[#0e0e0e]">{partnerDisplay.name || 'User'}</p>
                  <p className="text-[11px] text-[#a0a0a0]">{partnerDisplay.role === 'creative' ? 'Photographer' : 'Business'}</p>
                </div>
              </div>
              {/* Profile link */}
              {partnerDisplay.role === 'creative' && selectedConversation?.partner_profile_id && (
                <Link
                  to={`/photographers/${(selectedConversation.partner_user_name || 'photographer').replace(/\s+/g, '')}_${(selectedConversation.partner_profile_id || '').replace('profile_', '')}`}
                  className="inline-flex items-center gap-2 text-[12px] font-medium px-4 py-1.5 rounded-full border border-[#e4e1db] bg-white text-[#0e0e0e] hover:border-[#0e0e0e] transition-all"
                  data-testid="view-creative-profile-btn"
                >
                  <Briefcase className="w-3.5 h-3.5" /> View Portfolio
                </Link>
              )}
              {partnerDisplay.role === 'business' && (
                <Link
                  to={`/business/${(partnerDisplay.name || 'business').replace(/\s+/g, '')}_${(selectedPartner || '').replace('user_', '')}`}
                  className="inline-flex items-center gap-2 text-[12px] font-medium px-4 py-1.5 rounded-full border border-[#e4e1db] bg-white text-[#0e0e0e] hover:border-[#0e0e0e] transition-all"
                  data-testid="view-business-profile-btn"
                >
                  <Building2 className="w-3.5 h-3.5" /> View Business
                </Link>
              )}
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 px-5 py-4">
              <div className="space-y-3">
                {messages.map((msg) => (
                  <div key={msg.message_id} className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] md:max-w-[65%] px-4 py-2.5 rounded-[18px] ${
                      msg.sender_id === user?.user_id
                        ? 'bg-[#0e0e0e] text-white rounded-br-[4px]'
                        : 'bg-[#f7f5f2] text-[#0e0e0e] rounded-bl-[4px]'
                    }`}>
                      {msg.content && <p className="text-[13px] leading-relaxed">{msg.content}</p>}
                      {msg.file_url && (
                        msg.file_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                          <a href={msg.file_url} target="_blank" rel="noopener noreferrer">
                            <img src={msg.file_url} alt={msg.file_name} className="mt-1 max-w-[200px] rounded-[10px]" />
                          </a>
                        ) : (
                          <a href={msg.file_url} target="_blank" rel="noopener noreferrer"
                            className={`flex items-center gap-2 mt-1 text-[12px] underline ${msg.sender_id === user?.user_id ? 'text-white/80' : 'text-[#5a5a5a]'}`}>
                            <FileText className="w-4 h-4 flex-shrink-0" />
                            {msg.file_name}
                          </a>
                        )
                      )}
                      <p className={`text-[10px] mt-1 ${msg.sender_id === user?.user_id ? 'text-white/60' : 'text-[#a0a0a0]'}`}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <form onSubmit={sendMessage} className="px-5 py-4 border-t border-[#e4e1db] flex-shrink-0">
              {pendingFile && (
                <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-[#f7f5f2] border border-[#e4e1db] rounded-[14px] text-[12px]">
                  {pendingFile.previewUrl
                    ? <img src={pendingFile.previewUrl} alt="" className="h-9 w-9 object-cover rounded-[8px]" />
                    : <FileText className="w-4 h-4 text-[#5a5a5a]" />
                  }
                  <span className="flex-1 truncate text-[#5a5a5a]">{pendingFile.name}</span>
                  <button type="button" onClick={() => setPendingFile(null)} className="text-[#a0a0a0] hover:text-[#0e0e0e]">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.zip,.mp4,.mov,.webm" />
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  className="w-11 h-11 rounded-full border border-[#e4e1db] flex items-center justify-center text-[#5a5a5a] hover:border-[#0e0e0e] hover:text-[#0e0e0e] transition-all flex-shrink-0">
                  <Paperclip className="w-4 h-4" />
                </button>
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 h-11 rounded-full border-[#e4e1db]"
                  data-testid="message-input"
                />
                <button
                  type="submit"
                  disabled={sending || uploading || (!newMessage.trim() && !pendingFile)}
                  className="w-11 h-11 rounded-full bg-[#0e0e0e] text-white flex items-center justify-center hover:opacity-85 transition-opacity disabled:opacity-40 flex-shrink-0"
                  data-testid="send-message-btn"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-2.5">
            <div className="text-[36px] opacity-15">
              <svg width="44" height="44" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M17 4H3a1 1 0 00-1 1v9a1 1 0 001 1h3l2 3 2-3h7a1 1 0 001-1V5a1 1 0 00-1-1z"/>
              </svg>
            </div>
            <p className="text-[14px] text-[#a0a0a0]">Select a conversation to start messaging</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
